import React, { useMemo } from 'react'
import { Doughnut, Pie } from 'react-chartjs-2'
import { Card, CardContent } from '@mui/material'
import CustomLegend from './CustomLegend'

interface PieChartData {
  labels: string[]
  className?: string

  datasets: {
    data: number[]
    backgroundColor: string[]
    hoverBackgroundColor: string[]
  }[]
}

interface PieChartProps {
  title: string
  chartTitle: string
  data: PieChartData
  itemRow?: number
  className?: string
  /** Render chart only (no MUI Card / title) — for ma-card shells */
  embedded?: boolean
}

export function hasChartData(data?: {
  labels?: string[]
  datasets?: { data: number[] }[]
}): boolean {
  if (!data?.labels?.length || !data.datasets?.length) return false
  return data.datasets.some((dataset) =>
    dataset.data.some((value) => Number(value) > 0)
  )
}

type LegendRow = {
  label: string
  color: string
  value: number
  pct: number
  active: boolean
}

function buildLegendRows(data: PieChartData): LegendRow[] {
  const values = data.datasets[0]?.data ?? []
  const colors = data.datasets[0]?.backgroundColor ?? []
  const total = values.reduce((sum, v) => sum + (Number(v) || 0), 0)

  const rows = data.labels.map((label, i) => {
    const value = Number(values[i]) || 0
    return {
      label,
      color: colors[i] || '#8a9ba7',
      value,
      pct: total > 0 ? (value / total) * 100 : 0,
      active: value > 0,
    }
  })

  // Active slices first so the eye lands on what matters
  return rows.sort((a, b) => Number(b.active) - Number(a.active) || b.pct - a.pct)
}

const PieChart: React.FC<PieChartProps> = ({
  title,
  data,
  itemRow = 3,
  className = '',
  embedded = false,
}) => {
  const legendRows = useMemo(
    () => (hasChartData(data) ? buildLegendRows(data) : []),
    [data]
  )

  if (!hasChartData(data)) return null

  const dominant = legendRows.find((row) => row.active)
  const showCenter =
    !!dominant && legendRows.filter((row) => row.active).length === 1

  const pieOptions = {
    responsive: true,
    maintainAspectRatio: false,
    cutout: '64%',
    plugins: {
      legend: { display: false },
      tooltip: {
        enabled: true,
        callbacks: {
          label: (ctx: {
            label?: string
            parsed?: number
            dataset: { data: number[] }
          }) => {
            const value = Number(ctx.parsed) || 0
            const total = ctx.dataset.data.reduce(
              (sum, v) => sum + (Number(v) || 0),
              0
            )
            const pct = total > 0 ? Math.round((value / total) * 100) : 0
            return ` ${ctx.label ?? ''}: ${pct}%`
          },
        },
      },
    },
  }

  const chartBody = embedded ? (
    <div className="ma-pie-layout">
      <div className="ma-pie-visual">
        <div className="ma-chart-canvas ma-chart-canvas--pie">
          <Doughnut data={data} options={pieOptions} />
        </div>
        {showCenter && dominant ? (
          <div className="ma-pie-center">
            <strong>{Math.round(dominant.pct)}%</strong>
            <span title={dominant.label}>{dominant.label}</span>
          </div>
        ) : null}
      </div>
      <ul className="ma-pie-legend">
        {legendRows.map((row) => (
          <li
            key={row.label}
            className={`ma-pie-legend-item${row.active ? '' : ' is-muted'}`}
          >
            <span
              className="ma-pie-legend-swatch"
              style={{ background: row.color }}
            />
            <span className="ma-pie-legend-label" title={row.label}>
              {row.label}
            </span>
            <span className="ma-pie-legend-value">
              {row.active ? `${Math.round(row.pct)}%` : '—'}
            </span>
          </li>
        ))}
      </ul>
    </div>
  ) : (
    <>
      <div className="ma-chart-canvas ma-chart-canvas--pie">
        <Pie
          data={data}
          options={{
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              legend: { display: false },
              tooltip: { enabled: true },
            },
          }}
        />
      </div>
      <div
        style={{
          marginLeft:
            data.labels.length === 1
              ? '50%'
              : data.labels.length === 2
                ? '25%'
                : '15%',
        }}
      >
        <CustomLegend
          labels={data.labels}
          colors={data.datasets[0].backgroundColor}
          itemsPerRow={itemRow}
        />
      </div>
    </>
  )

  if (embedded) {
    return <div className={className}>{chartBody}</div>
  }

  return (
    <Card
      style={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        height: '100%',
        padding: '20px',
        width: '100%',
        maxWidth: '100%',
        maxHeight: '100%',
      }}
      className={className}
    >
      <CardContent style={{ textAlign: 'center' }}>
        <h5 style={{ marginBottom: '20px', height: '50px' }}>{title}</h5>
        {chartBody}
      </CardContent>
    </Card>
  )
}

export default PieChart
